import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Descriptions,
  InputNumber,
  message,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import dayjs from "dayjs";
import {
  FIRESTORE_ACQUISITION_DEFAULTS_DOC,
  FIRESTORE_LEADS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
  FIRESTORE_SETTINGS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { addDoc, collection, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import ProcessShipmentsProgressModal from "./ProcessShipmentsProgressModal";
import axios from "axios";
import urls from "../../../../../urls";
import { getFirebaseIDToken } from "../../../../../functions/firebase/firebase";
import { ORDER_STATUS_CREATED } from "../../../../../constants/OrderConstant";
import { PAYMENT_METHOD_DIRECT_DEBIT } from "../../../../../constants/PaymentConstants";
import { LEAD_STATUS_TRIAL_SENT } from "../../../../../constants/LeadConstant";
import { DeleteOutlined } from "@ant-design/icons";

const { Title, Text } = Typography;

const PreviewBulkShipmentsModal = ({ visible, onCancel, onSuccess, leads = [], products = [] }) => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const [loading, setLoading] = useState(false);
  const [defaultProducts, setDefaultProducts] = useState([]);
  const [defaultProductsModalOpen, setDefaultProductsModalOpen] = useState(false);
  const [defaultProductsDraft, setDefaultProductsDraft] = useState([]);
  const [newDefaultProduct, setNewDefaultProduct] = useState(null);
  const [defaultProductsLoading, setDefaultProductsLoading] = useState(false);

  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentLead, setCurrentLead] = useState(null);
  const [currentOperation, setCurrentOperation] = useState(null);
  const [completedShipments, setCompletedShipments] = useState([]);
  const [isProcessingComplete, setIsProcessingComplete] = useState(false);
  const [processingResults, setProcessingResults] = useState(null);

  const acquisitionDefaultsRef = doc(
    firestore,
    FIRESTORE_REGIONS_TABLE,
    COMPANY_COUNTRY_CODE,
    FIRESTORE_SETTINGS_TABLE,
    FIRESTORE_ACQUISITION_DEFAULTS_DOC,
  );

  const loadDefaultShipmentProducts = async () => {
    try {
      const snap = await getDoc(acquisitionDefaultsRef);
      if (!snap.exists()) return [];
      const parsed = snap.data()?.defaultShipmentProducts;
      if (!Array.isArray(parsed)) return [];
      return parsed
        .filter((item) => item && typeof item.productId === "string" && typeof item.quantity === "number")
        .map((item) => ({ productId: item.productId, quantity: Math.max(1, Math.floor(item.quantity)) }));
    } catch (e) {
      console.error("Failed to load default shipment products from Firestore:", e);
      return [];
    }
  };

  const saveDefaultShipmentProducts = async (items) => {
    try {
      await setDoc(acquisitionDefaultsRef, { defaultShipmentProducts: items }, { merge: true });
    } catch (e) {
      console.error("Failed to save default shipment products to Firestore:", e);
    }
  };

  const refreshDefaultProducts = async () => {
    const loaded = await loadDefaultShipmentProducts();
    setDefaultProducts(loaded);
  };

  useEffect(() => {
    if (!visible) return;
    refreshDefaultProducts();
  }, [visible]);

  const openDefaultProductsModal = async () => {
    setDefaultProductsLoading(true);
    const defaults = await loadDefaultShipmentProducts();
    const draftWithNames = defaults
      .map(({ productId, quantity }) => {
        const product = products?.find((p) => p.productId === productId);
        return { productId, quantity, productName: product?.productName || "" };
      })
      .filter((item) => !!item.productId);
    setDefaultProductsDraft(draftWithNames);
    setNewDefaultProduct(null);
    setDefaultProductsLoading(false);
    setDefaultProductsModalOpen(true);
  };

  const defaultProductsDropdownOptions = [...(products || [])]
    .sort((a, b) => (a.productName || "").localeCompare(b.productName || ""))
    .map((p) => ({ productId: p.productId, productName: p.productName }));

  const processShipments = async () => {
    try {
      setLoading(true);

      // Close preview modal and open progress modal
      onCancel();
      setShowProgressModal(true);
      setProgress({ current: 0, total: leads.length });
      setCompletedShipments([]);
      setIsProcessingComplete(false);
      setCurrentLead(null);
      setCurrentOperation(null);

      const results = {
        successful: [],
        partial: [],
        failed: [],
        total: leads.length,
      };

      // Get default products
      const defaultCartItems = defaultProducts
        .map(({ productId, quantity }) => {
          const product = products?.find((p) => p.productId === productId);
          if (!product) return null;
          return {
            externalId: product.exactId?.toString() || "",
            itemCount: quantity,
            orderable: true,
            productCategory: product.category?.en || "",
            productId: product.productId,
            productImageDownloadURL: product.productImage512DownloadURL?.[0] || "",
            productName: product.productName,
            productPrice: "0.00",
            productVat: 0,
            productBarcode: product.barcode || "",
          };
        })
        .filter(Boolean);

      // Process leads sequentially
      for (let i = 0; i < leads.length; i++) {
        const lead = leads[i];

        // Update current lead being processed
        setCurrentLead(lead);
        setProgress({ current: i, total: leads.length });

        // Track operations for this shipment
        const shipmentOperations = {
          firestore: { attempted: false, success: false, error: null, response: null },
          stock: { attempted: false, success: false, error: null, response: null },
          sooCool: { attempted: false, success: false, error: null, response: null },
        };

        let farmorderId = null;

        try {
          // 1. CREATE SHIPMENT IN FIRESTORE
          setCurrentOperation("firestore");
          shipmentOperations.firestore.attempted = true;

          // Get delivery time and charge from lead's time slot
          let deliveryTime = "10:00-18:00";
          let deliveryTimeCharge = 0;
          let deliveryTimeSlotObject = lead.deliveryTimeSlot || null;

          if (deliveryTimeSlotObject) {
            deliveryTime = `${deliveryTimeSlotObject.start}-${deliveryTimeSlotObject.end}`;
            deliveryTimeCharge = deliveryTimeSlotObject.deliverySurcharge || 0;
          }

          const orderObj = {
            type: "acquisition",
            leadId: lead.leadId,
            farmorderCustomerName: lead.companyName,
            customerDiscount: 0,
            farmorderRegion: lead.region || "NL",
            deliveryCostThreshold: 0,
            paymentMethod: PAYMENT_METHOD_DIRECT_DEBIT,
            deliveryTime: deliveryTime,
            deliveryTimeCharge: deliveryTimeCharge,
            deliveryTimeSlot: deliveryTimeSlotObject,
            deliveryNotes: "",
            deliveryLabel: lead.deliveryLabel,
            farmorderComments: "",
            farmorderDeliveryDate: lead.desiredDeliveryDate,
            farmorderStatus: ORDER_STATUS_CREATED,
            farmorderUserFirstName: lead.contactFirstName || "",
            farmorderUserLastName: lead.contactLastName || "",
            userLanguage: "en",
            userEmail: lead.email || "",
            farmorderUserId: "",
            deliveryBy: "Chef's Farm",
            farmorderDateTime: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
            userShoppingCart: defaultCartItems,
            orderDateTime: dayjs().unix() * 1000,
            subTotalAmount: 0,
            subTotalWithoutDiscount: 0,
            invoiceAdditionalCosts: 0,
            shippingCost: "0",
            totalAmount: 0,
            tax: 0,
            createdBy: `CMS - ${email}`,
            shippingCostDisabled: true,
          };

          const docRef = await addDoc(
            collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE),
            orderObj,
          );

          farmorderId = docRef.id;

          await updateDoc(
            doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, farmorderId),
            { farmorderId: farmorderId, updatedAt: dayjs().unix(), updatedBy: email },
          );

          shipmentOperations.firestore.success = true;

          // UPDATE LEAD STATUS TO TRIAL_SENT
          try {
            await updateDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_LEADS_TABLE, lead.leadId),
              {
                status: LEAD_STATUS_TRIAL_SENT,
                shipmentCreated: true,
              },
            );
          } catch (leadError) {
            console.error("Error updating lead status:", leadError);
          }

          // 2. CALL APIS IN PARALLEL
          const processToken = await getFirebaseIDToken();

          const apiPromises = [];
          let stockResponseData = null;
          let sooCoolResponseData = null;

          const orderPayload = {
            ...orderObj,
            farmorderId,
            id: farmorderId,
          };

          // Stock API
          setCurrentOperation("stock");
          shipmentOperations.stock.attempted = true;
          apiPromises.push(
            axios
              .post(urls.stockApiUrl, orderPayload, { headers: { Authorization: processToken } })
              .then((res) => {
                stockResponseData = res?.data || null;
                shipmentOperations.stock.response = stockResponseData;
                if (stockResponseData && typeof stockResponseData.success === "boolean") {
                  shipmentOperations.stock.success = !!stockResponseData.success;
                  if (!stockResponseData.success) {
                    shipmentOperations.stock.error = stockResponseData.message || t("stock_api_returned_an_error");
                  }
                } else {
                  shipmentOperations.stock.success = true;
                }
              })
              .catch((error) => {
                shipmentOperations.stock.error = error.response?.data?.message || error.message;
                shipmentOperations.stock.response = error.response?.data || null;
              }),
          );

          // SooCool API (if applicable)
          const sooCoolLabels = ["SCH", "SCM", "SCO"];
          if (sooCoolLabels.includes(lead.deliveryLabel)) {
            setCurrentOperation("sooCool");
            shipmentOperations.sooCool.attempted = true;
            apiPromises.push(
              axios
                .post(urls.sooCoolOrder, orderPayload, { headers: { Authorization: processToken } })
                .then((res) => {
                  sooCoolResponseData = res?.data || null;
                  shipmentOperations.sooCool.response = sooCoolResponseData;
                  shipmentOperations.sooCool.success = true;
                })
                .catch((error) => {
                  shipmentOperations.sooCool.error = error.response?.data?.message || error.message;
                  shipmentOperations.sooCool.response = error.response?.data || null;
                }),
            );
          }

          // Wait for all API calls to complete
          await Promise.all(apiPromises);

          // Determine overall status
          const allSuccess =
            shipmentOperations.firestore.success &&
            shipmentOperations.stock.success &&
            (!shipmentOperations.sooCool.attempted || shipmentOperations.sooCool.success);

          const anyApiFailure =
            !shipmentOperations.stock.success ||
            (shipmentOperations.sooCool.attempted && !shipmentOperations.sooCool.success);

          if (allSuccess) {
            results.successful.push({ lead, farmorderId, operations: shipmentOperations });
            setCompletedShipments((prev) => [
              ...prev,
              {
                lead,
                status: "success",
                farmorderId,
                operations: { ...shipmentOperations },
              },
            ]);
          } else if (shipmentOperations.firestore.success && anyApiFailure) {
            results.partial.push({ lead, farmorderId, operations: { ...shipmentOperations }, needsCleanup: false });
            setCompletedShipments((prev) => [
              ...prev,
              {
                lead,
                status: "partial",
                farmorderId,
                operations: { ...shipmentOperations },
              },
            ]);
          } else {
            results.failed.push({ lead, farmorderId, operations: shipmentOperations });
            setCompletedShipments((prev) => [
              ...prev,
              {
                lead,
                status: "error",
                farmorderId,
                operations: shipmentOperations,
              },
            ]);
          }
        } catch (error) {
          console.error(`Error processing shipment for ${lead.companyName}:`, error);

          if (!shipmentOperations.firestore.attempted) {
            shipmentOperations.firestore.attempted = true;
          }
          shipmentOperations.firestore.error = error.message;
          results.failed.push({ lead, error: error.message, operations: shipmentOperations });

          setCompletedShipments((prev) => [
            ...prev,
            {
              lead,
              status: "error",
              error: error.message,
              operations: shipmentOperations,
            },
          ]);
        }
      }

      // Update final progress
      setProgress({ current: leads.length, total: leads.length });
      setCurrentLead(null);
      setCurrentOperation(null);
      setIsProcessingComplete(true);

      // Set processing results for summary
      setProcessingResults({
        successCount: results.successful.length,
        partialCount: results.partial.length,
        errorCount: results.failed.length,
        total: results.total,
      });

      // Call onSuccess if any shipments were processed successfully
      if (results.successful.length > 0) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error processing shipments:", error);
      message.error(t("notifications.error_creating_shipment") + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleProgressModalClose = () => {
    setShowProgressModal(false);
    setProgress({ current: 0, total: 0 });
    setCompletedShipments([]);
    setIsProcessingComplete(false);
    setCurrentLead(null);
    setCurrentOperation(null);
    setProcessingResults(null);
  };

  const renderCollapseHeader = (lead, index) => {
    return (
      <Space>
        <Text strong>
          #{index + 1} {lead.companyName},
        </Text>
        <Text>{dayjs(lead.desiredDeliveryDate).format("ddd DD-MM-YYYY")}</Text>
      </Space>
    );
  };

  return (
    <>
      <Modal
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {t("preview_bulk_shipments_modal_title")}
            </Title>
            <Tag color="processing">
              {leads.length} {t("shipments").toLowerCase()}
            </Tag>
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        onOk={processShipments}
        okText={t("create_shipments")}
        okButtonProps={{ disabled: defaultProducts.length === 0 }}
        cancelText={t("cancel")}
        width={900}
        style={{ top: 20 }}
        confirmLoading={loading}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">{t("preview_bulk_shipments_modal_message")}</Text>
        </div>

        {(() => {
          const resolvedProducts = defaultProducts
            .map(({ productId, quantity }) => {
              const product = products?.find((p) => p.productId === productId);
              return product ? { productName: product.productName, quantity } : null;
            })
            .filter(Boolean);

          const configureButton = (
            <Button size="small" onClick={openDefaultProductsModal} loading={defaultProductsLoading}>
              {t("configure_default_products")}
            </Button>
          );

          if (resolvedProducts.length === 0) {
            return (
              <Alert
                type="warning"
                showIcon
                message={t("default_products")}
                description={t("no_default_products_configured")}
                action={configureButton}
                style={{ marginBottom: 16 }}
              />
            );
          }

          return (
            <Alert
              type="info"
              showIcon
              message={t("default_products")}
              action={configureButton}
              description={
                <div>
                  <div style={{ marginBottom: 4 }}>{t("default_products_in_shipments")}</div>
                  <ul style={{ margin: "4px 0 0 0", paddingLeft: 20 }}>
                    {resolvedProducts.map((item, index) => (
                      <li key={index}>
                        {item.quantity}x {item.productName}
                      </li>
                    ))}
                  </ul>
                </div>
              }
              style={{ marginBottom: 16 }}
            />
          );
        })()}

        {leads.length > 0 ? (
          <div>
            <Collapse
              ghost
              accordion
              size="small"
              style={{ background: "transparent" }}
              items={leads.map((lead, index) => {
                return {
                  key: index,
                  label: renderCollapseHeader(lead, index),
                  children: (
                    <Card size="small" style={{ marginTop: 8 }}>
                      <Descriptions size="small" column={2}>
                        <Descriptions.Item label={t("company_name")}>
                          <Text>{lead.companyName}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("delivery_date")}>
                          <Text>{dayjs(lead.desiredDeliveryDate).format("dddd DD-MM-YYYY")}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("contact_person")}>
                          <Text>
                            {lead.contactFirstName && lead.contactLastName
                              ? `${lead.contactFirstName} ${lead.contactLastName}`
                              : lead.contactFirstName || lead.contactLastName || "-"}
                          </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("delivery_label")}>
                          <Text>{lead.deliveryLabel}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={t("time_slot")}>
                          <Text>
                            {lead.deliveryTimeSlot
                              ? `${lead.deliveryTimeSlot.start} - ${lead.deliveryTimeSlot.end}${
                                  lead.deliveryTimeSlot.deliverySurcharge > 0
                                    ? ` (+€${lead.deliveryTimeSlot.deliverySurcharge})`
                                    : ""
                                }`
                              : "-"}
                          </Text>
                        </Descriptions.Item>
                      </Descriptions>
                    </Card>
                  ),
                };
              })}
            />
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Text type="secondary">{t("no_leads_selected")}</Text>
          </div>
        )}
      </Modal>

      <Modal
        title={t("configure_default_products")}
        open={defaultProductsModalOpen}
        onCancel={() => setDefaultProductsModalOpen(false)}
        zIndex={1200}
        okText={t("save")}
        cancelText={t("cancel")}
        onOk={async () => {
          const cleaned = (defaultProductsDraft || [])
            .filter((item) => item?.productId)
            .map((item) => ({
              productId: item.productId,
              quantity: Math.max(1, Math.floor(Number(item.quantity || 1))),
            }));
          await saveDefaultShipmentProducts(cleaned);
          setDefaultProductsModalOpen(false);
          await refreshDefaultProducts();
        }}
      >
        <div style={{ marginBottom: "12px" }}>{t("default_products_description")}</div>

        <div style={{ display: "flex", gap: "8px", marginBottom: "12px" }}>
          <Select
            style={{ flex: 1 }}
            placeholder={t("add_default_product")}
            showSearch
            filterOption={(input, option) => {
              const productData = defaultProductsDropdownOptions.find((p) => p.productId === option.value);
              return productData?.productName?.toLowerCase().includes(input.toLowerCase());
            }}
            onChange={(value) => {
              if (!value) return;
              const product = products?.find((p) => p.productId === value);
              if (!product) return;
              if (defaultProductsDraft.some((p) => p.productId === product.productId)) {
                setNewDefaultProduct(null);
                return;
              }
              setDefaultProductsDraft((prev) => [
                ...prev,
                { productId: product.productId, productName: product.productName, quantity: 1 },
              ]);
              setNewDefaultProduct(null);
            }}
            value={newDefaultProduct}
          >
            {defaultProductsDropdownOptions
              .filter((p) => !defaultProductsDraft.some((d) => d.productId === p.productId))
              .map((p) => (
                <Select.Option key={p.productId} value={p.productId}>
                  {p.productName}
                </Select.Option>
              ))}
          </Select>
        </div>

        {defaultProductsDraft?.length ? (
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {defaultProductsDraft.map((item) => (
              <div
                key={item.productId}
                style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}
              >
                <div style={{ flex: 1 }}>{item.productName || item.productId}</div>
                <InputNumber
                  min={1}
                  value={item.quantity}
                  onChange={(value) => {
                    setDefaultProductsDraft((prev) =>
                      prev.map((p) => (p.productId === item.productId ? { ...p, quantity: Number(value || 1) } : p)),
                    );
                  }}
                />
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    setDefaultProductsDraft((prev) => prev.filter((p) => p.productId !== item.productId));
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div>{t("no_default_products_set")}</div>
        )}

        <div style={{ marginTop: 16, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
          <Button
            danger
            onClick={async () => {
              await saveDefaultShipmentProducts([]);
              setDefaultProductsDraft([]);
              await refreshDefaultProducts();
              setDefaultProductsModalOpen(false);
            }}
          >
            {t("clear_defaults")}
          </Button>
        </div>
      </Modal>

      <ProcessShipmentsProgressModal
        visible={showProgressModal}
        onClose={handleProgressModalClose}
        progress={progress}
        currentLead={currentLead}
        currentOperation={currentOperation}
        completedShipments={completedShipments}
        isComplete={isProcessingComplete}
        summary={processingResults}
      />
    </>
  );
};

export default PreviewBulkShipmentsModal;
